import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Skeleton,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Add as AddIcon,
  TrendingUp as TrendingIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { agencyApi } from '../services/api';
import logger from '../utils/logger';

const StatCard = ({ title, value, icon, color, loading }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: 3,
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      height: '100%',
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: `${color}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {React.cloneElement(icon, { sx: { color, fontSize: 24 } })}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
            {title}
          </Typography>
          {loading ? (
            <Skeleton width={60} height={36} />
          ) : (
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
              {value}
            </Typography>
          )}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { agency, user } = useAuth();
  const [stats, setStats] = useState({
    subaccountCount: 0,
    maxSubaccounts: 5,
    userCount: 0,
  });
  const [recentSubaccounts, setRecentSubaccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, subaccountsRes, usersRes] = await Promise.all([
        agencyApi.getProfile(),
        agencyApi.getSubaccounts(),
        agencyApi.getUsers(),
      ]);

      if (profileRes.data.success) {
        setStats({
          subaccountCount: profileRes.data.agency.subaccountCount || 0,
          maxSubaccounts: profileRes.data.agency.maxSubaccounts || 5,
          userCount: usersRes.data.users?.length || 1,
        });
      }

      if (subaccountsRes.data.success) {
        setRecentSubaccounts(subaccountsRes.data.subaccounts?.slice(0, 5) || []);
      }
    } catch (error) {
      logger.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Welcome Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 1 }}>
          Welcome back, {user?.name || 'User'}!
        </Typography>
        <Typography variant="body1" sx={{ color: '#666' }}>
          Here's what's happening with your agency today.
        </Typography>
      </Box>

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Active Subaccounts"
            value={stats.subaccountCount}
            icon={<BusinessIcon />}
            color="#06AB8B"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Available Slots"
            value={stats.maxSubaccounts - stats.subaccountCount}
            icon={<TrendingIcon />}
            color="#2196f3"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Team Members"
            value={stats.userCount}
            icon={<GroupIcon />}
            color="#9c27b0"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Grid container spacing={3}>
        {/* Create Subaccount Card - Only show for owner/admin */}
        {(user?.role === 'owner' || user?.role === 'admin') && (
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                background: 'linear-gradient(135deg, #06AB8B 0%, #049572 100%)',
                color: 'white',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AddIcon sx={{ fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Create New Subaccount
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {stats.maxSubaccounts - stats.subaccountCount} slots remaining
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
                Launch a new branded subaccount for your client with custom branding,
                domain, and features.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/subaccounts/create')}
                disabled={stats.subaccountCount >= stats.maxSubaccounts}
                sx={{
                  bgcolor: 'white',
                  color: '#06AB8B',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                }}
              >
                Create Subaccount
              </Button>
            </Paper>
          </Grid>
        )}

        {/* Recent Subaccounts */}
        <Grid item xs={12} md={(user?.role === 'owner' || user?.role === 'admin') ? 6 : 12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              height: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a2e' }}>
                Recent Subaccounts
              </Typography>
              <Button
                size="small"
                onClick={() => navigate('/subaccounts')}
                sx={{ color: '#06AB8B' }}
              >
                View All
              </Button>
            </Box>

            {loading ? (
              <Box>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} height={50} sx={{ mb: 1 }} />
                ))}
              </Box>
            ) : recentSubaccounts.length > 0 ? (
              <Box>
                {recentSubaccounts.map((sub) => (
                  <Box
                    key={sub.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 1.5,
                      borderRadius: 2,
                      '&:hover': { bgcolor: '#f5f7fa' },
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        bgcolor: '#06AB8B15',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {sub.logo_url ? (
                        <img
                          src={sub.logo_url}
                          alt={sub.name}
                          style={{
                            width: 24,
                            height: 24,
                            objectFit: 'contain',
                          }}
                        />
                      ) : (
                        <BusinessIcon sx={{ color: '#06AB8B', fontSize: 20 }} />
                      )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        onClick={() => navigate(`/subaccounts/${sub.id}/edit`)}
                        sx={{
                          fontWeight: 500,
                          color: '#1a1a2e',
                          cursor: 'pointer',
                          '&:hover': { color: '#06AB8B', textDecoration: 'underline' },
                        }}
                      >
                        {sub.name || sub.brand_name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        {sub.slug}.domain.com
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 4,
                  color: '#666',
                }}
              >
                <BusinessIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                <Typography variant="body2">No subaccounts yet</Typography>
                <Typography variant="caption">
                  Create your first subaccount to get started
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Plan Info */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mt: 3,
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          bgcolor: '#f8f9fa',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
              Current Plan
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a2e' }}>
              {agency?.plan?.charAt(0).toUpperCase() + agency?.plan?.slice(1) || 'Starter'} Plan
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
              Subaccount Usage
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#06AB8B' }}>
              {stats.subaccountCount} / {stats.maxSubaccounts}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default Dashboard;
